import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  useTheme,
} from '@mui/material';
import React, { ReactElement, useEffect, useId, useState } from 'react';
import CheckboxComponent from '../CheckboxComponent';
import TextFieldComponent from '../TextFieldComponent';
import { backgroundColorDarkTheme } from '../../styles/global.styles';

interface IDropSelectsComponentProps {
  label: string;
  options: IDropSelectOptions[];
  valueSelected: any;
  onChange: (value: any) => void;
  isMultiple?: boolean;
  helperText?: string;
  size?: 'small' | 'medium';
  disabled?: boolean;
  fullwidth?: boolean;
  isError?: boolean;
  isRequired?: boolean;
  errorMessage?: string;
  requiredMessage?: string;
  additionalButton?: ReactElement;
  mb?: string | number;
}

export interface IDropSelectOptions {
  value: any;
  option: string;
  description?: string;
}

/**
 * 🚨 @deprecated ESTE COMPONENTE ESTÁ OBSOLETO E SERÁ REMOVIDO EM BREVE.
 * * Por favor, não utilize este componente para novas telas. 
 * Utilize o `DropdownComponentV2` (para listas curtas) ou o `DropdownSearchbleComponent` (para busca)
 * Utilize o 'DropdownMultipleComponent (para listas com vários selecionaveis e busca)'.
 * * @see DropdownComponentV2
 * @see DropdownSearchbleComponent
 * @see DropdownMultipleComponent
 */
const DropSelectComponent = ({
  label,
  options,
  valueSelected,
  onChange,
  isMultiple = false,
  helperText,
  size = 'medium',
  disabled = false,
  fullwidth = true,
  isError = false,
  isRequired = true,
  errorMessage,
  requiredMessage,
  additionalButton,
  mb,
}: IDropSelectsComponentProps) => {
  const theme = useTheme();
  const componentId = useId();
  const [open, setOpen] = useState<boolean>(false);

  const [optionValues, setOptionValues] = useState<IDropSelectOptions[]>([]);

  const [isSelectAll, setIsSelectAll] = useState<boolean>(false);

  const [search, setSearch] = useState<string>('');
  const [inputSearch, setInputSearch] = useState<string>('');

  useEffect(() => {
    setOptionValues(options);
  }, [options]);

  useEffect(() => {
    if (
      isMultiple &&
      Array.isArray(optionValues) &&
      Array.isArray(valueSelected)
    ) {
      const allSelected =
        optionValues.length > 0 && valueSelected.length === optionValues.length;
      setIsSelectAll(allSelected);
    }
  }, [valueSelected, optionValues]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearch(inputSearch);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [inputSearch]);

  useEffect(() => {
    if (search.trim() !== '') {
      setOptionValues(
        options.filter((o) =>
          o.option.toUpperCase().includes(search.toUpperCase()),
        ),
      );
    } else {
      setOptionValues(options);
    }
  }, [search, options]);

  const handleChange = ({ target: { value } }: SelectChangeEvent<any>) => {
    onChange(value);
  };

  const handleRender = (selection: any) => {
    if (isMultiple) {
      if (!Array.isArray(selection) || selection.length === 0) {
        return <>Nenhum</>;
      }
      const selectedOptions = options.filter((o) =>
        selection.includes(o.value),
      );
      return selectedOptions.map((o) => o.option).join(', ');
    } else {
      if (!selection || selection === '') {
        return <>Nenhum</>;
      }
      const selectedOption = options.find((o) => o.value === selection);
      return selectedOption ? selectedOption.option : '';
    }
  };

  const handleSelectAll = (val: boolean) => {
    if (!isMultiple) return;
    const selection = val ? optionValues.map((o) => o.value) : [];
    onChange(selection);
  };

  return (
    <Box sx={{ width: 'auto', display: 'flex', mb: mb }}>
      <FormControl
        sx={{ margin: 'auto', minWidth: '180px' }}
        fullWidth={fullwidth}
        size={size}
        disabled={disabled}
        error={isError}
        required={isRequired}
      >
        <InputLabel id={componentId} shrink={true} sx={{ color: '#ffffff' }}>
          {label}
        </InputLabel>
        <Select
          labelId={componentId}
          id={`select-${componentId}`}
          displayEmpty
          value={valueSelected ?? (isMultiple ? [] : '')}
          label={label}
          onChange={handleChange}
          multiple={isMultiple}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          renderValue={handleRender}
          /* ... */
        >
          <Box
            sx={{
              backgroundColor: backgroundColorDarkTheme,
              p: 1,
              mb: 0,
              pb: 0,
            }}
          >
            <Box>
              <TextFieldComponent
                placeholder={'Filtrar...'}
                onChangeText={(val: string) => setInputSearch(val)}
                value={inputSearch}
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
                size={'small'}
              />
            </Box>
            {isMultiple && (
              <Box>
                <CheckboxComponent
                  label={isSelectAll ? 'Limpar Seleção' : 'Selecionar Todos'}
                  value={isSelectAll}
                  onChange={(val) => handleSelectAll(val)}
                />
              </Box>
            )}
            <Box>
              <Divider
                orientation={'horizontal'}
                sx={{ borderColor: 'gray', mb: 1 }}
              />
            </Box>
          </Box>
          {!isMultiple && (
            <MenuItem value="">
              <em>Nenhum</em>
            </MenuItem>
          )}
          {optionValues.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {isMultiple && (
                <Checkbox
                  checked={
                    Array.isArray(valueSelected) &&
                    valueSelected.includes(opt.value)
                  }
                />
              )}
              <ListItemText
                primary={opt.option}
                secondary={opt.description ?? ''}
              />
            </MenuItem>
          ))}
        </Select>
        {isRequired && !isError && (
          <FormHelperText>
            {requiredMessage ?? 'Campo Obrigatório'}
          </FormHelperText>
        )}
        {isError && (
          <FormHelperText>{errorMessage ?? 'Campo inválido'}</FormHelperText>
        )}
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
      {additionalButton}
    </Box>
  );
};

export default DropSelectComponent;
